import { useContext } from "react";
import { useSelector } from "react-redux";
import { selectDisplayCurrency } from "@shared/slices/common.slice";
import { usePrecisions } from "@shared/utils/hooks/usePrecisions";
import { BotFormContext } from "@shared/features/bots/BotForm/common/BotForm.context";
import { getLocaleStringWithPrecision, toFixed } from "@shared/utils/numbers";
import { OrderTypes } from "@shared/features/trades/common/types";
import {
  Error,
  StrategyOrder,
} from "@shared/features/bots/BotForm/common/types";
import { DISPLAY_CURRENCIES, DOLLAR_COINS } from "@shared/common/constants";
import { TypographyType } from "@shared/common/Typography/Typography";
import { BotType, PrecisionType } from "@shared/types";

interface UseOrderSummaryProps {
  order: StrategyOrder;
  index: number;
  showAmount?: boolean;
  showPrice?: boolean;
  firstOrderDistanceText?: string;
  orderType: OrderTypes;
}

const useOrderSummary = ({
  order,
  index,
  showAmount,
  showPrice,
  firstOrderDistanceText,
  orderType,
}: UseOrderSummaryProps) => {
  const displayCurrency = useSelector(selectDisplayCurrency);
  const { getPrecision } = usePrecisions();
  const {
    errors,
    botAmountData,
    symbolPriceData,
    botType,
    useSymbolFromAlert,
  } = useContext(BotFormContext)!;
  const { data: currentPrice, precision } = symbolPriceData || {};
  const isGridBot = botType === BotType.GRID;
  const isTradingViewSignalsBot = botType === BotType.TRADING_VIEW_SIGNALS;

  let orderPrice = toFixed(order.price, precision?.price || 0);
  if (orderPrice > 1000000 || orderPrice < 0) orderPrice = 0;

  const amountKey =
    displayCurrency === DISPLAY_CURRENCIES.BTC ? "btcAmount" : "usdAmount";
  const precisionKey =
    displayCurrency === DISPLAY_CURRENCIES.BTC
      ? PrecisionType.BTC_AMOUNT
      : PrecisionType.USD_AMOUNT;
  const amountPrecision = getPrecision(precisionKey) || 0;
  const amount = order[amountKey];
  const mainCoin = botAmountData?.mainCoin;
  const hasActiveEntriesAmountError = errors.includes(
    Error.ACTIVE_ENTRIES_AMOUNT
  );
  const isImmediate = order.isImmediate && index !== 0;

  const pricesError =
    orderType === OrderTypes.ENTRY
      ? Error.ENTRY_PRICES
      : Error.TAKE_PROFIT_PRICES;
  let amountTextType: TypographyType = "primary";
  let showWarningIcon = false;
  if (!order.isValid) {
    amountTextType = "danger";
  } else if (
    order.availableAmountWarning &&
    !errors.includes(Error.MIN_ENTRY_ORDER_AMOUNT) &&
    !errors.includes(pricesError) &&
    !hasActiveEntriesAmountError
  ) {
    amountTextType = "warning";
    showWarningIcon = true;
  }
  if (
    showAmount &&
    hasActiveEntriesAmountError &&
    (isImmediate || order.isActive) &&
    (!isGridBot || index !== 0)
  ) {
    amountTextType = "danger";
  }

  const getAmount = () => {
    const amountData: { amount?: string; ratio?: string; text?: string } = {};
    if (index === 0 && isGridBot) {
      amountData.text = "No Entry";
    } else if (showAmount) {
      amountData.amount = getLocaleStringWithPrecision(amount, amountPrecision);
      amountData.ratio = `${toFixed(order.ratio * 100, 2)}%`;
    } else {
      amountData.ratio = `${toFixed(order.ratio * 100, 2)}%`;
    }
    return amountData;
  };

  const getPriceDifference = () => {
    const orderPriceDifference = currentPrice
      ? toFixed(((currentPrice - orderPrice) / currentPrice) * -100, 2)
      : 0;
    return `${orderPriceDifference}%`;
  };

  const getPriceData = () => {
    let distance;
    if (order.isPending) distance = "Pending";
    else {
      distance =
        index === 0 && firstOrderDistanceText
          ? `${firstOrderDistanceText}`
          : getPriceDifference();
    }
    const priceData: { price?: number; distance?: string } = {
      distance: `(${distance})`,
    };
    if (showPrice) {
      priceData.price = orderPrice;
    }
    return priceData;
  };

  const getErrorData = () => {
    let errorData: { title: string; error: string } | undefined;
    if (!order.isValid && !hasActiveEntriesAmountError && !useSymbolFromAlert) {
      let usdAmount;
      if (order.minAmount && mainCoin && !DOLLAR_COINS.includes(mainCoin)) {
        usdAmount = botAmountData.convertMainCoinAmountToUSD(
          order.minAmount,
          true
        );
      }
      errorData = {
        title: "Min amount:",
        error: `${order.minAmount} ${mainCoin ?? ""}${
          usdAmount ? ` (${usdAmount} USD)` : ""
        }`,
      };
    }
    return errorData;
  };

  return {
    priceData: getPriceData(),
    amountData: getAmount(),
    errorData: getErrorData(),
    amountTextType,
    showWarningIcon,
    showIsImmediate: isGridBot,
    isImmediate,
    isTradingViewSignalsBot,
    useSymbolFromAlert,
  };
};

export default useOrderSummary;
